import type { LovCxpiDTO, ModuleDTO } from '@/features/api/extranet/dto/Demande/CXPI'
import type { FraisDTO } from '@/features/api/extranet/dto/Demande/shared'
import { DRF_TAUX_HOR } from '@/features/domaine/constantes'
import type { ModeFraisNature } from '@/features/domaine/types/opco'
import {
  BaseCalculFrais,
  ModeFrais,
  type NatureFrais,
} from '@/features/domaine/types/opco/Demande'
import {
  HelpFraisAnnexe,
  HelpFraisPedagogique,
  NatureFraisAvecQuantite,
  NaturesFrais,
  TypeModule,
  TypeModuleLabel,
} from '@/features/domaine/types/opco/Demande/CXPI'
import { type DemandeStore, NB_MAX_MODULES } from '@/features/interface/demande/shared'
import type { FraisDataParMode } from '@/features/interface/demande/shared/components/frais/Frais.types'
import { useDialog } from '@/features/interface/dialog'
import { isNumber } from '@/features/shared/tools'
import type { IconName } from '@designSystem/fondation/icons/iconsList'
import { storeToRefs } from 'pinia'
import { computed, ref, watch, type InjectionKey } from 'vue'

type ModuleAction = ReturnType<typeof useModuleActions>
export const CXPI_MODULES_ACTIONS: InjectionKey<ModuleAction> = Symbol('CPXI_MODULE_ACTIONS')

function initialiseListeFrais(mode: ModeFraisNature) {
  //if (typeDemande === 'ReservationFonds') return null

  const natureFrais = NaturesFrais[mode]
  return natureFrais.map((nature) => {
    const baseCalcul: BaseCalculFrais = NatureFraisAvecQuantite.includes(nature) ? 'Quantite' : 'Montant'
    return {
      mode,
      nature,
      baseCalcul,
      montant: null,
      quantite: null,
    } as FraisDTO
  })
}

export interface ModuleConfig {
  limite: number
  label: string
  icon: IconName
}

export interface MontantDemande {
  heuresDemandees: number
  taux: number
  nombreContrats: number
  montantHt: number
}

export const MODULE_CONFIG: Record<TypeModule, ModuleConfig> = {
  BilanPositionnement: {
    limite: 1,
    label: TypeModuleLabel.BilanPositionnement,
    icon: 'eye',
  },
  Externe: {
    limite: NB_MAX_MODULES,
    label: TypeModuleLabel.Externe,
    icon: 'bank',
  },
  BilanEvaluation: {
    limite: 1,
    label: TypeModuleLabel.BilanEvaluation,
    icon: 'eye',
  },
} as const

export function useModuleActions(
  store: DemandeStore<'MODEL_CXPI', LovCxpiDTO>,
) {
  const { model, readonly, estReservation } = storeToRefs(store)
  const { setupCustomUpdater } = store
  const dialog = useDialog()

  const activeModuleKey = ref<string | null>(null)
  const activeModuleIndex = computed({
    get() {
        if (!activeModuleKey.value) return null
        return modules.value.findIndex((module) => module.id === activeModuleKey.value) ?? null
    },
    set(value) {
      activeModuleKey.value = modules.value[value ?? -1]?.id ?? null
    }
  })
  const afficherModale = ref(false)

  const modules = computed(() => model.value?.formation?.modules || [])

  const nombreModules = computed(() => {
    return TypeModule.map((typeModule) => {
      return [typeModule, modules.value.filter((module) => module.typeModule === typeModule).length] as [
        TypeModule,
        number,
      ]
    }).reduce(
      (acc, value) => {
        acc[value[0]] = value[1]
        return acc
      },
      {} as Record<TypeModule, number>,
    )
  })

  const creerTypeModuleVide = (typeModule: TypeModule): ModuleDTO => {
    return {
      id: crypto.randomUUID(),
      typeModule,
      etablissementPrestataire: null,
      lieuFormationIdentiqueCfaPrincipal: false,
      lieuFormation: {
        voie: null,
        complement: null,
        commune: null,
        codePostal: null,
        mentionsSpeciales: null,
      },
      formationRealiseeDansEntreprise: null,
      modalitePedagogique: null,
      dureeFoad: null,
      duree: null,
      dateDebut: null,
      dateFin: null,
      horairesFormation: null,
      fraisPedagogiques: initialiseListeFrais('Pedagogique'),
      fraisAnnexesSalarie: initialiseListeFrais('Annexe'),
    }
  }

  const patchAjouterModule = async (typeModule: TypeModule) => {
    const moduleAjouteUpdater = setupCustomUpdater<(value: number | PromiseLike<number>) => void>((patch, resolve) => {
      const nouveauModule = creerTypeModuleVide(typeModule)
      if (!patch.formation?.modules) patch.formation!.modules = []

      if (typeModule === 'BilanPositionnement') {
        patch.formation!.modules!.splice(0, 0, nouveauModule)
        resolve(0)
      } else if (typeModule === 'BilanEvaluation') {
        patch.formation!.modules!.push(nouveauModule)
        resolve(patch.formation!.modules!.length - 1)
      } else if (typeModule === 'Externe') {
        const pos = patch.formation!.modules!.findIndex((m) => m.typeModule === 'BilanEvaluation')
        if (pos === -1) {
          patch.formation!.modules!.push(nouveauModule)
          resolve(patch!.formation!.modules.length - 1)
        } else {
          patch.formation!.modules!.splice(pos, 0, nouveauModule)
          resolve(pos)
        }
      }
    })

    const result = new Promise<number>((resolve) => {
      moduleAjouteUpdater(resolve)
    })
    return await result
  }

  async function ajouterModule(typeModule: TypeModule) {
    activeModuleIndex.value = await patchAjouterModule(typeModule)
    afficherModale.value = false
  }

  const définirModuleEnCours = (index: number | null) => {
    activeModuleIndex.value = index
  }

  async function supprimerModule(index: number) {
    const module = listeModules.value[index]!

    const messageConfirmation: string = estReservation.value
      ? `Confirmez-vous la suppression du module de type ${MODULE_CONFIG[module.typeModule!].label} ?`
      : `Un bilan était prévu. Confirmez-vous la suppression du module de type ${MODULE_CONFIG[module.typeModule!].label} ? Un autre module ne pourra pas être ajouté.`

    const result = await dialog.showDialog({
      message: messageConfirmation,
      type: 'YesNo',
      title: 'Supprimer un module',
    })

    if (result === 'Yes') {
      await supprimerModuleDuModel(index)

      if (activeModuleIndex.value === index) {
        activeModuleIndex.value = null
      } else if (activeModuleIndex.value !== null && activeModuleIndex.value > index) {
        activeModuleIndex.value -= 1
      }
    }
  }

  const supprimerModuleDuModel = setupCustomUpdater<number>((patch, index) => {
    const element = patch.formation?.modules?.splice(index, 1)
    if (!element) throw 'Index inexistant'
  })

  const listeModules = computed<ModuleDTO[]>(() => {
    return model.value!.formation!.modules!
  })

  const moduleEnCours = computed<ModuleDTO | undefined>(() => {
    return listeModules.value![activeModuleIndex.value!]
  })

  const metaModuleEnCours = computed(() => {
    if (!isNumber(activeModuleIndex.value)) return null
    return store.meta.formation?.modules?.at(activeModuleIndex.value)
  })

  const fraisDataParMode = computed<FraisDataParMode>(() => {
    return {
      Pedagogique: {
        frais: moduleEnCours.value?.fraisPedagogiques || [],
        metaFrais: metaModuleEnCours.value?.fraisPedagogiques,
        fraisTitre: 'Frais Pédagogiques',
        fraisField: 'fraisPedagogiques',
        fraisHelp: HelpFraisPedagogique,
      },
      Annexe: {
        frais: moduleEnCours.value?.fraisAnnexesSalarie || [],
        metaFrais: metaModuleEnCours.value?.fraisAnnexesSalarie,
        fraisTitre: 'Frais Annexes Salarié',
        fraisField: 'fraisAnnexesSalarie',
        fraisHelp: HelpFraisAnnexe,
      },
    }
  })

  const modifieFrais = setupCustomUpdater<{
    mode: ModeFrais
    nature: NatureFrais
    field: keyof FraisDTO
    valeur?: string | number | null
  }>((patch, { mode, nature, field, valeur }) => {
    const modulePatch = patch.formation!.modules![activeModuleIndex.value!]!

    const fraisField = fraisDataParMode.value[mode]?.fraisField as keyof ModuleDTO

    const listeFrais = modulePatch[fraisField] as FraisDTO[]

    const fraisAModifier = listeFrais.find((f) => f.nature === nature)
    if (!fraisAModifier) {
      throw new Error(`Aucun frais trouvé pour la nature ${nature}`)
    }
    fraisAModifier[field] = valeur as any
  })

  const selectedModuleIndex = ref<number | null>(null)

  const setActiveCard = (index: number) => {
    selectedModuleIndex.value = selectedModuleIndex.value === index ? null : index
  }

  function getModuleLabel(typeModule: TypeModule) {
    return MODULE_CONFIG[typeModule].label
  }

  const cards = computed(() =>
    TypeModule.map((typeModule) => {
      return {
        title: typeModule === 'Externe' ? `Formation ${getModuleLabel(typeModule)}` : getModuleLabel(typeModule),
        iconName: MODULE_CONFIG[typeModule].icon,
        disabled: nombreModules.value[typeModule] >= MODULE_CONFIG[typeModule].limite,
        typeModule,
      }
    }),
  )

  watch(
    () => afficherModale.value,
    (newVal) => newVal && (selectedModuleIndex.value = null),
  )

  const setAdresseIdentiqueCfaPrincipal = setupCustomUpdater<boolean>((patch, value) => {
    const module = patch.formation!.modules![activeModuleIndex.value!]!
    module.lieuFormationIdentiqueCfaPrincipal = value
    module.lieuFormation ??= {
      voie: null,
      complement: null,
      commune: null,
      codePostal: null,
      mentionsSpeciales: null,
    }
  })

  const listeMontantsDemandes = computed(() => {
    return TypeModule.reduce(
      (acc, typeModule) => {
        // on récupère tous les modules de ce type
        const modulesDuType = modules.value.filter((m) => m.typeModule === typeModule)

        const heuresDemandees = modulesDuType.reduce((acc, m) => acc + (m.duree ?? 0), 0)

        const taux = DRF_TAUX_HOR

        const nombreContrats = model.value?.contrat?.nombreContrats ?? 0

        const montantHt = heuresDemandees * taux * nombreContrats

        acc[typeModule] = {
          heuresDemandees,
          taux,
          nombreContrats,
          montantHt,
        }

        return acc
      },
      {} as Record<TypeModule, MontantDemande>,
    )
  })

  return {
    // États et actions liés aux modules
    afficherModale,
    activeModuleIndex,
    nombreModules,
    ajouterModule,
    définirModuleEnCours,
    supprimerModule,

    setActiveCard,
    cards,
    selectedModuleIndex,

    listeModules,
    moduleEnCours,
    metaModuleEnCours,
    getModuleLabel,
    // Actions liées aux frais
    modifieFrais,
    readonly,
    fraisDataParMode,

    listeMontantsDemandes,

    // Modif lieu formation
    setAdresseIdentiqueCfaPrincipal,
  }
}
